/**
 * 인증 상태 (계획서 §11, §12.1).
 *
 * 토큰은 server-side opaque session cookie 이므로 브라우저 저장소에 아무것도 두지 않는다.
 * 이 store 는 "현재 누구로 로그인되어 있는가" 만 들고 있다.
 */

import { create } from "zustand";

import { ApiError, fetchCurrentUser, login as loginRequest, logout as logoutRequest } from "@/api/client";
import type { CurrentUser } from "@/types/api";

export type AuthPhase = "unknown" | "authenticated" | "anonymous";

interface AuthState {
  phase: AuthPhase;
  user: CurrentUser | null;
  /** 로그인 실패 메시지. 계정 존재 여부를 노출하지 않는 서버 문구를 그대로 쓴다. */
  error: string | null;
  pending: boolean;

  restore: () => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const GENERIC_LOGIN_ERROR = "아이디 또는 비밀번호가 올바르지 않습니다.";

export const useAuthStore = create<AuthState>((set) => ({
  phase: "unknown",
  user: null,
  error: null,
  pending: false,

  restore: async () => {
    try {
      const user = await fetchCurrentUser();
      set({ phase: "authenticated", user, error: null });
    } catch {
      set({ phase: "anonymous", user: null });
    }
  },

  login: async (username, password) => {
    set({ pending: true, error: null });
    try {
      const user = await loginRequest(username, password);
      set({ phase: "authenticated", user, pending: false, error: null });
      return true;
    } catch (error) {
      const message =
        error instanceof ApiError && error.status !== 401
          ? error.message
          : GENERIC_LOGIN_ERROR;
      set({ phase: "anonymous", user: null, pending: false, error: message });
      return false;
    }
  },

  logout: async () => {
    set({ pending: true });
    try {
      await logoutRequest();
    } finally {
      set({ phase: "anonymous", user: null, pending: false, error: null });
    }
  },
}));
